import { batch } from 'react-redux';
import { identity } from 'fp-ts/function';
import {
    AsyncAction,
    CommanderThunkAction,
    WithAccountId,
    WithBillId,
    WithEnterpriseId,
    WithName,
} from '@mews-commander/types';
import {
    AccountType,
    BillAddParameters,
    BillExtent,
    BillFilterParameters,
    BillResult,
    BillState,
    Company,
    Customer,
    GroupBillingResult,
} from '@mews-commander/data-access';
import { createFetchAction, callApiInChunks } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { setBills, deleteBills, deleteBillsByOwnerIds } from '../ducks/bills';
import { setCurrentEnterpriseCashiers } from '../ducks/cashiers';
import prefix from '../prefix';
import { deletePayments, getPaymentIdsByBillIds, setPayments } from '../ducks/payments';
import { deleteOrderItems, getOrderItemIdsByBillIds, setOrderItems } from '../ducks/orderItems';
import { setProducts } from '../ducks/products';
import { setServiceOrders } from '../ducks/serviceOrders';
import { setServices } from '../ducks/services';
import { setPaymentCards } from '../ducks/paymentCards';
import { setProfiles } from '../ducks/profiles';
import { setServiceOrderNotes } from '../ducks/serviceOrderNotes';
import { setCustomers } from '../ducks/customers';
import { setCompanies } from '../ducks/companies';

const FETCH_BILLS = prefix('FETCH_BILLS');
const ADD_BILL = prefix('ADD_BILL');
const DELETE_BILL = prefix('DELETE_BILL');
const UPDATE_BILL = prefix('UPDATE_BILL');

export const DEFAULT_BILL_FETCH_EXTENT: BillExtent = {
    cashiers: false,
    notes: false,
    orderItems: false,
    paymentCards: false,
    paymentProfiles: false,
    payments: false,
    products: false,
    serviceOrders: false,
    services: false,
    associatedBills: false,
};

export const FULL_BILL_EXTENT: Partial<BillExtent> & { bills: boolean } = {
    bills: true,
    orderItems: true,
    products: true,
    payments: true,
    paymentCards: true,
    serviceOrders: true,
};

export const BILLING_EXTENT: Partial<BillExtent> = {
    orderItems: true,
    payments: true,
    paymentCards: true,
    products: true,
    serviceOrders: true,
    services: true,
};

const fetch = (params: BillFilterParameters & WithEnterpriseId) =>
    (async (dispatch, getState, { repositories }) => {
        const { enterpriseId, extent } = params;

        const promise = repositories.bills.getAll(params).then((data) => {
            const {
                associatedBills,
                bills,
                cashiers,
                payments,
                orderItems,
                products,
                serviceOrders,
                services,
                paymentCards,
                paymentProfiles,
                // @ts-expect-error Will be fixed in the backend
                correctiveBills,
                notes,
                correctedBills,
                billProfiles,
            } = data;

            batch(() => {
                const loadOrderItems = extent?.orderItems ?? false;
                const loadPayments = extent?.payments ?? false;

                const state = getState();

                const billsArr = [...(bills ?? []), ...(associatedBills ?? [])];

                if (billsArr?.length) {
                    const billIds = billsArr.map((bill) => bill.id);

                    if (loadPayments) {
                        const oldPaymentIds = getPaymentIdsByBillIds(state, { enterpriseId, billIds });
                        dispatch(deletePayments({ ids: oldPaymentIds, scope: enterpriseId }));
                    }

                    if (loadOrderItems) {
                        const oldOrderItemIds = getOrderItemIdsByBillIds(state, { billIds });
                        dispatch(deleteOrderItems(oldOrderItemIds));
                    }

                    dispatch(setBills(billsArr));
                }

                if (cashiers?.length) {
                    dispatch(setCurrentEnterpriseCashiers(cashiers));
                }

                if (payments?.length) {
                    // @ts-expect-error
                    dispatch(setPayments(payments));
                }

                if (orderItems?.length) {
                    // @ts-expect-error
                    dispatch(setOrderItems(orderItems));
                }

                if (products?.length) {
                    // @ts-expect-error
                    dispatch(setProducts(products));
                }

                if (serviceOrders?.length) {
                    dispatch(setServiceOrders({ serviceOrders }));
                }

                if (services?.length) {
                    dispatch(setServices(services));
                }

                if (paymentCards?.length) {
                    // @ts-expect-error
                    dispatch(setPaymentCards(paymentCards));
                }

                if (paymentProfiles?.length) {
                    dispatch(setProfiles(paymentProfiles));
                }

                if (notes?.length) {
                    // @ts-expect-error
                    dispatch(setServiceOrderNotes(notes));
                }

                if (correctedBills?.length) {
                    dispatch(setBills(correctedBills));
                }

                if (billProfiles?.length) {
                    dispatch(setProfiles(billProfiles));
                }

                if (correctiveBills?.length) {
                    dispatch(setBills(correctiveBills));
                }
            });

            return data;
        });

        return dispatch(createFetchAction(FETCH_BILLS, promise));
    }) satisfies CommanderThunkAction<AsyncAction<BillResult>>;

// This will be eventually replaced by BE logic - should be used only from account billing screen
const fetchBillsForAccountBilling = (params: BillFilterParameters & WithEnterpriseId) =>
    (async (dispatch, getState, { repositories }) => {
        const { enterpriseId, extent } = params;

        const promise = repositories.billingScreen.getBillsByAccount(params).then((data) => {
            const {
                associatedBills,
                bills,
                cashiers,
                payments,
                orderItems,
                products,
                serviceOrders,
                services,
                paymentCards,
                paymentProfiles,
                // @ts-expect-error Will be fixed in the backend
                correctiveBills,
                notes,
                correctedBills,
                billProfiles,
            } = data;

            batch(() => {
                const loadOrderItems = extent?.orderItems ?? false;
                const loadPayments = extent?.payments ?? false;

                const state = getState();

                const billsArr = [...(bills ?? []), ...(associatedBills ?? [])];

                if (billsArr?.length) {
                    const billIds = billsArr.map((bill) => bill.id);

                    if (loadPayments) {
                        const oldPaymentIds = getPaymentIdsByBillIds(state, { enterpriseId, billIds });
                        dispatch(deletePayments({ ids: oldPaymentIds, scope: enterpriseId }));
                    }

                    if (loadOrderItems) {
                        const oldOrderItemIds = getOrderItemIdsByBillIds(state, { billIds });
                        dispatch(deleteOrderItems(oldOrderItemIds));
                    }

                    dispatch(setBills(billsArr));
                }

                if (cashiers?.length) {
                    dispatch(setCurrentEnterpriseCashiers(cashiers));
                }

                if (payments?.length) {
                    // @ts-expect-error
                    dispatch(setPayments(payments));
                }

                if (orderItems?.length) {
                    // @ts-expect-error
                    dispatch(setOrderItems(orderItems));
                }

                if (products?.length) {
                    // @ts-expect-error
                    dispatch(setProducts(products));
                }

                if (serviceOrders?.length) {
                    dispatch(setServiceOrders({ serviceOrders }));
                }

                if (services?.length) {
                    dispatch(setServices(services));
                }

                if (paymentCards?.length) {
                    // @ts-expect-error
                    dispatch(setPaymentCards(paymentCards));
                }

                if (paymentProfiles?.length) {
                    dispatch(setProfiles(paymentProfiles));
                }

                if (notes?.length) {
                    // @ts-expect-error
                    dispatch(setServiceOrderNotes(notes));
                }

                if (correctedBills?.length) {
                    dispatch(setBills(correctedBills));
                }

                if (billProfiles?.length) {
                    dispatch(setProfiles(billProfiles));
                }

                if (correctiveBills?.length) {
                    dispatch(setBills(correctiveBills));
                }
            });

            return data;
        });

        return dispatch(createFetchAction(FETCH_BILLS, promise));
    }) satisfies CommanderThunkAction<AsyncAction<BillResult>>;

export const fetchBillsForGroupBillingWithFiltering = (
    params: BillFilterParameters & WithEnterpriseId & { groupId?: string }
) =>
    (async (dispatch, getState, { repositories }) => {
        const { groupId, languageCode, cultureCode, enterpriseId } = params;

        const promise = repositories.billing
            .getGroupBilling({
                languageCode,
                cultureCode,
                enterpriseId,
                reservationGroupId: groupId,
            })
            .then((data) => {
                const { bills, payments, orderItems, products, serviceOrders, services, paymentCards, accounts } = data;

                const accountIds = accounts?.map((it) => it.id) || [];

                const customers: Customer[] = [];
                const companies: Company[] = [];

                accounts?.forEach((account) => {
                    const accountData = account.data;
                    if (accountData.discriminator === AccountType.Customer) {
                        customers.push(accountData.value);
                    }
                    if (accountData.discriminator === AccountType.Company) {
                        companies.push(accountData.value);
                    }
                });

                batch(() => {
                    dispatch(deleteBillsByOwnerIds(accountIds));

                    if (companies.length) {
                        dispatch(setCompanies(companies));
                    }

                    if (customers.length) {
                        dispatch(setCustomers(customers));
                    }

                    if (bills?.length) {
                        dispatch(setBills(bills));
                    }

                    if (payments?.length) {
                        // @ts-expect-error
                        dispatch(setPayments(payments));
                    }

                    if (orderItems?.length) {
                        // @ts-expect-error
                        dispatch(setOrderItems(orderItems));
                    }

                    if (products?.length) {
                        // @ts-expect-error
                        dispatch(setProducts(products));
                    }

                    if (serviceOrders?.length) {
                        dispatch(setServiceOrders({ serviceOrders }));
                    }

                    if (services?.length) {
                        dispatch(setServices(services));
                    }

                    if (paymentCards?.length) {
                        // @ts-expect-error
                        dispatch(setPaymentCards(paymentCards));
                    }
                });

                return data;
            });

        return dispatch(createFetchAction(FETCH_BILLS, promise));
    }) satisfies CommanderThunkAction<AsyncAction<GroupBillingResult>>;

export const fetchCurrentEnterpriseBillsByIds =
    ({
        billIds,
        accountIds,
        state,
        type,
        extent,
        reservationGroupId,
    }: { accountIds?: string[]; billIds: string[] } & Pick<
        BillFilterParameters,
        'state' | 'type' | 'extent' | 'reservationGroupId'
    >): CommanderThunkAction<Promise<void>> =>
    async (dispatch) => {
        const currentEnterpriseId = getCurrentEnterpriseId();

        if (billIds.length === 0) {
            await dispatch(
                fetch({
                    ownerIds: accountIds,
                    state,
                    type,
                    extent,
                    enterpriseId: currentEnterpriseId,
                    reservationGroupId,
                })
            );
            return;
        }

        await callApiInChunks({
            apiCall: async (ids: string[]) => {
                await dispatch(
                    fetch({
                        ownerIds: accountIds,
                        billIds: ids,
                        state,
                        type,
                        extent,
                        enterpriseId: currentEnterpriseId,
                    })
                );
            },
            chunkSize: 1000,
            processChunks: identity<string[][]>,
        })(billIds);
    };

interface fetchCurrentEnterpriseBillsByAccountIdsProps extends Pick<BillFilterParameters, 'state' | 'type' | 'extent'> {
    accountIds: string[];
    groupId?: string;
}

// This will be eventually replaced by BE logic - should be used only from account billing screen
export const fetchOpenBillsByAccountIdForAccountBilling = ({
    accountId,
    extent = { ...DEFAULT_BILL_FETCH_EXTENT, ...BILLING_EXTENT },
}: WithAccountId & { extent?: BillExtent }): CommanderThunkAction =>
    fetchCurrentEnterpriseBillsByAccountIdsforAccountBilling({
        accountIds: [accountId],
        state: BillState.Open,
        extent,
    });

export const fetchCurrentEnterpriseBillsByAccountIds =
    ({
        accountIds,
        state,
        type,
        extent,
    }: fetchCurrentEnterpriseBillsByAccountIdsProps): CommanderThunkAction<AsyncAction<BillResult>> =>
    async (dispatch) => {
        const currentEnterpriseId = getCurrentEnterpriseId();
        return dispatch(fetch({ ownerIds: accountIds, state, type, extent, enterpriseId: currentEnterpriseId }));
    };

// This will be eventually replaced by BE logic - should be used only from account billing screen
export const fetchCurrentEnterpriseBillsByAccountIdsforAccountBilling =
    ({
        accountIds,
        state,
        type,
        extent,
    }: fetchCurrentEnterpriseBillsByAccountIdsProps): CommanderThunkAction<AsyncAction<BillResult>> =>
    async (dispatch) => {
        const currentEnterpriseId = getCurrentEnterpriseId();
        return dispatch(
            fetchBillsForAccountBilling({
                ownerIds: accountIds,
                state,
                type,
                extent,
                enterpriseId: currentEnterpriseId,
            })
        );
    };

type AddBillProps = Pick<BillAddParameters, 'name' | 'companyId' | 'associatedAccountId' | 'accountId'>;

export const addBill =
    ({ accountId, name, companyId, associatedAccountId }: AddBillProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const enterpriseId = getCurrentEnterpriseId();

        if (!accountId) {
            throw new Error('Cannot add bill because of missing accountId');
        }

        const payload = {
            bills: [
                {
                    ownerId: accountId,
                    companyId,
                    associatedAccountId,
                    name,
                },
            ],
            enterpriseId,
        };

        const promise = repositories.bills.add(payload).then(({ bills }) => {
            if (bills) {
                dispatch(setBills(bills));
                return bills?.[0]?.id;
            }
        });

        return dispatch(createFetchAction(ADD_BILL, promise));
    };

export const deleteBill =
    (billId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const enterpriseId = getCurrentEnterpriseId();
        const billIds = [billId];

        const payload = {
            billIds,
            enterpriseId,
        };

        const promise = repositories.bills.delete(payload).then(() => {
            dispatch(deleteBills(billIds));
        });

        return dispatch(createFetchAction(DELETE_BILL, promise));
    };

export const updateBillName =
    ({ name, billId }: WithName & WithBillId): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const enterpriseId = getCurrentEnterpriseId();

        const billUpdates = [
            {
                billId,
                name: {
                    value: name,
                },
            },
        ];

        const promise = repositories.bills.update({ enterpriseId, billUpdates }).then(({ bills }) => {
            if (bills) {
                dispatch(setBills(bills));
            }
        });

        return dispatch(createFetchAction(UPDATE_BILL, promise));
    };
